<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">訂單詳情</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回列表
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchOrder">重試</button>
    </div>

    <div v-else-if="order" class="order-detail">
      <!-- 訂單基本資訊 -->
      <div class="detail-section">
        <h2 class="section-title">訂單基本資訊</h2>
        <div class="detail-grid">
          <div class="detail-item">
            <label>訂單編號</label>
            <span>{{ order.orderNumber || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>訂單日期</label>
            <span>{{ formatDate(order.orderDate) }}</span>
          </div>
          <div class="detail-item">
            <label>訂單來源</label>
            <span class="badge badge-info">{{ getOrderSourceText(order.orderSource) }}</span>
          </div>
          <div class="detail-item">
            <label>訂單狀態</label>
            <span :class="getStatusBadgeClass(order.logisticsStatus || order.status)">
              {{ getStatusText(order.logisticsStatus || order.status) }}
            </span>
          </div>
          <div class="detail-item">
            <label>付款狀態</label>
            <span :class="getPaymentStatusBadgeClass(order.paymentStatus)">
              {{ getPaymentStatusText(order.paymentStatus) }}
            </span>
          </div>
          <div class="detail-item">
            <label>付款方式</label>
            <span>{{ getPaymentMethodText(order.paymentMethod) }}</span>
          </div>
          <div class="detail-item">
            <label>配送編號</label>
            <span>{{ shipmentNumber(order) }}</span>
          </div>
          <div v-if="order.shippingMethod === 'ecpay'" class="detail-item">
            <label>託運單列印時間</label>
            <span>{{ formatDate(order.logisticsPrintedAt) }}</span>
          </div>
          <div v-if="order.shippingMethod === 'ecpay'" class="detail-item">
            <label>物流取消時間</label>
            <span>{{ formatDate(order.logisticsCancelledAt) }}</span>
          </div>
          <div v-if="canPrintLogistics(order)" class="detail-item">
            <label>託運單</label>
            <button type="button" class="btn btn-sm btn-secondary" :disabled="printing" @click="printLogistics">
              {{ printing ? '列印中' : '列印託運單' }}
            </button>
          </div>
          <div class="detail-item">
            <label>建立時間</label>
            <span>{{ formatDate(order.createdAt) }}</span>
          </div>
          <div class="detail-item">
            <label>建立人員</label>
            <span>{{ order.createdByName || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>最後修改時間</label>
            <span>{{ formatDate(order.updatedAt) }}</span>
          </div>
          <div class="detail-item">
            <label>最後修改人員</label>
            <span>{{ order.updatedByName || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 會員資訊 -->
      <div v-if="order.memberId" class="detail-section">
        <h2 class="section-title">會員資訊</h2>
        <div class="detail-grid">
          <div class="detail-item">
            <label>會員姓名</label>
            <span>{{ order.memberName || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>會員編號</label>
            <span>{{ order.memberCode || '-' }}</span>
          </div>
          <div class="detail-item" v-if="order.memberPhone">
            <label>會員電話</label>
            <span>{{ order.memberPhone }}</span>
          </div>
        </div>
      </div>

      <!-- 金額資訊 -->
      <div class="detail-section">
        <h2 class="section-title">金額資訊</h2>
        <div class="detail-grid">
          <div class="detail-item">
            <label>小計</label>
            <span>{{ formatCurrency(order.subtotal) }}</span>
          </div>
          <div class="detail-item">
            <label>折扣金額</label>
            <span>{{ formatCurrency(order.discountAmount || 0) }}</span>
          </div>
          <div class="detail-item">
            <label>運費</label>
            <span>{{ formatCurrency(order.shippingFee || 0) }}</span>
          </div>
          <div class="detail-item">
            <label>稅額</label>
            <span>{{ formatCurrency(order.taxAmount || 0) }}</span>
          </div>
          <div class="detail-item">
            <label>總金額</label>
            <span class="text-bold">{{ formatCurrency(order.totalAmount) }}</span>
          </div>
          <div class="detail-item">
            <label>已付金額</label>
            <span>{{ formatCurrency(order.paidAmount || 0) }}</span>
          </div>
        </div>
      </div>

      <!-- 優惠券使用紀錄 -->
      <div v-if="order.couponUsages && order.couponUsages.length > 0" class="detail-section">
        <h2 class="section-title">
          <i class="ri-coupon-line"></i>
          優惠券使用紀錄
        </h2>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>優惠券名稱</th>
                <th>優惠券代碼</th>
                <th>類型</th>
                <th>折扣</th>
                <th>實際折抵</th>
                <th>使用時間</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="usage in order.couponUsages" :key="usage.id">
                <td>{{ usage.templateName || '-' }}</td>
                <td>
                  <span class="coupon-code-badge">{{ usage.couponCode }}</span>
                </td>
                <td>{{ getCouponTypeText(usage.couponType) }}</td>
                <td>{{ formatCouponDiscount(usage) }}</td>
                <td>
                  <span v-if="usage.discountAmount" class="text-danger-strong">
                    -{{ formatCurrency(usage.discountAmount) }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td>{{ formatDate(usage.usedAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="detail-section">
        <h2 class="section-title">狀態調整</h2>
        <p class="status-adjust-hint">
          準備出貨：履約中、待出貨；已下單：訂單成立／已接單完成；已出貨：已交付物流業者。
        </p>
        <div class="inventory-lifecycle-card">
          <div class="inventory-lifecycle-title">庫存處理狀態</div>
          <div class="inventory-lifecycle-content">
            <span :class="getInventoryLifecycleBadgeClass(order)">
              {{ getInventoryLifecycleText(order) }}
            </span>
            <p class="inventory-lifecycle-hint">{{ getInventoryLifecycleHint(order) }}</p>
            <div v-if="canHandlePaidOutOfStock(order)" class="inventory-lifecycle-actions">
              <button
                class="btn btn-warning"
                :disabled="compensationUpdating"
                @click="handlePaidOutOfStockCompensation('backorder')"
              >
                <span v-if="compensationUpdating">處理中...</span>
                <span v-else>轉預購處理</span>
              </button>
              <button
                class="btn btn-danger"
                :disabled="compensationUpdating"
                @click="handlePaidOutOfStockCompensation('refunded')"
              >
                <span v-if="compensationUpdating">處理中...</span>
                <span v-else>標記已退款</span>
              </button>
            </div>
          </div>
        </div>
        <div class="payment-update-row">
          <div class="detail-item">
            <label>訂單狀態</label>
            <select v-model="editingOrderStatus" class="form-control" @change="handleOrderStatusChange">
              <option value="pending_fulfillment">待履約</option>
              <option value="preparing">備貨中</option>
              <option value="shipped">已出貨</option>
              <option value="arrived_store">已到店</option>
              <option value="picked_up">客戶已取貨</option>
              <option value="delivered">宅配已送達</option>
              <option value="returned_unclaimed">到店未取退回</option>
              <option value="returned_customer">客戶退貨</option>
            </select>
          </div>
          <div class="detail-item">
            <label>付款狀態</label>
            <select v-model="editingPaymentStatus" class="form-control" @change="handlePaymentStatusChange">
              <option value="pending">待付款</option>
              <option value="paid">已付款</option>
              <option value="failed">付款失敗</option>
              <option value="refunded">已退款</option>
            </select>
          </div>
          <div class="detail-item">
            <label>已付金額</label>
            <input
              v-model.number="editingPaidAmount"
              type="number"
              class="form-control"
              min="0"
              step="0.01"
            />
          </div>
          <div class="payment-update-actions">
            <button
              class="btn btn-primary"
              :disabled="statusUpdating"
              @click="handleUpdateOrderStatus"
            >
              <span v-if="statusUpdating">更新中...</span>
              <span v-else>儲存狀態</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 點數資訊 -->
      <div class="detail-section">
        <h2 class="section-title">點數資訊</h2>
        <div class="detail-grid">
          <div class="detail-item">
            <label>獲得點數</label>
            <span>{{ formatNumber(order.pointsEarned || 0) }}</span>
          </div>
          <div v-if="order.pointsEarnSummary?.activeEarnPoints > 0" class="detail-item">
            <label>可用點數</label>
            <span class="text-primary">{{ formatNumber(order.pointsEarnSummary.activeEarnPoints) }}</span>
          </div>
          <div v-if="order.pointsEarnSummary?.pendingEarnPoints > 0" class="detail-item">
            <label>待生效點數</label>
            <span class="text-warning-strong">{{ formatNumber(order.pointsEarnSummary.pendingEarnPoints) }}</span>
          </div>
          <div v-if="order.pointsEarnSummary?.pendingEffectiveDate" class="detail-item">
            <label>生效日期</label>
            <span>{{ formatEffectiveDate(order.pointsEarnSummary.pendingEffectiveDate) }}</span>
          </div>
          <div class="detail-item">
            <label>使用點數</label>
            <span>{{ formatNumber(order.pointsRedeemed || 0) }}</span>
          </div>
          <div class="detail-item">
            <label>點數狀態</label>
            <span>{{ getOrderPointsStatusText(order) }}</span>
          </div>
        </div>
      </div>

      <!-- 訂單明細 -->
      <div v-if="orderItems && orderItems.length > 0" class="detail-section">
        <h2 class="section-title">訂單明細</h2>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>商品名稱</th>
                <th>SKU</th>
                <th>數量</th>
                <th>單價</th>
                <th>折扣</th>
                <th>小計</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in orderItems" :key="item.id">
                <tr
                  class="item-row"
                  :class="{ expandable: !!item.productId, expanded: expandedItemId === item.id }"
                  @click="item.productId ? toggleItemDetail(item) : null"
                >
                  <td class="expand-cell">
                    <span v-if="item.productId" class="expand-icon">
                      <i :class="expandedItemId === item.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </span>
                  </td>
                  <td>
                    <span class="product-name-cell">
                      {{ item.productName || '-' }}
                      <span v-if="item.variantId" class="variant-badge">規格</span>
                    </span>
                  </td>
                  <td>{{ item.productSku || '-' }}</td>
                  <td>{{ item.quantity || 0 }}</td>
                  <td>{{ formatCurrency(item.unitPrice) }}</td>
                  <td>{{ formatCurrency(item.discountAmount || 0) }}</td>
                  <td>{{ formatCurrency(item.subtotal) }}</td>
                </tr>

                <!-- 展開的商品資料面板 -->
                <tr v-if="expandedItemId === item.id" class="item-detail-row">
                  <td colspan="7" class="item-detail-cell">
                    <div v-if="productDetailLoading" class="product-detail-loading">
                      <div class="spinner spinner-sm"></div>
                      <span>載入商品資料中...</span>
                    </div>
                    <div v-else-if="productDetail" class="product-detail-panel">
                      <div class="product-detail-main">
                        <!-- 主圖 -->
                        <div v-if="productDetail.imageUrl" class="product-detail-image">
                          <img :src="productDetail.imageUrl" :alt="productDetail.name" />
                        </div>
                        <div v-else class="product-detail-image product-detail-image--empty">
                          <i class="ri-image-line"></i>
                        </div>

                        <!-- 基本資訊 -->
                        <div class="product-detail-info">
                          <div class="product-detail-name">{{ productDetail.name }}</div>
                          <div class="product-detail-meta">
                            <span v-if="productDetail.sku" class="meta-item">
                              <i class="ri-barcode-line"></i> {{ productDetail.sku }}
                            </span>
                            <span class="meta-item">
                              <i class="ri-price-tag-3-line"></i> {{ formatCurrency(productDetail.unitPrice) }}
                            </span>
                            <span class="meta-item status-badge"
                              :class="productDetail.status === 'active' ? 'status-active' : 'status-inactive'">
                              {{ productDetail.status === 'active' ? '上架中' : '已下架' }}
                            </span>
                          </div>
                          <div v-if="productDetail.summary" class="product-detail-desc">
                            {{ productDetail.summary }}
                          </div>

                          <!-- 選取的規格 -->
                          <div v-if="item.variantId && productDetail.variants && productDetail.variants.length" class="product-variant-highlight">
                            <div class="variant-highlight-title">本次訂購規格</div>
                            <div v-for="v in productDetail.variants.filter(v => v.id === item.variantId)" :key="v.id" class="variant-highlight-item">
                              <span class="variant-name">{{ v.variantName || v.sku }}</span>
                              <span v-if="v.specialPrice != null && v.specialPrice !== ''" class="variant-price">{{ formatCurrency(v.specialPrice) }}</span>
                              <span v-else-if="v.unitPrice != null" class="variant-price">{{ formatCurrency(v.unitPrice) }}</span>
                              <span v-else class="variant-price variant-price--inherited">同主商品售價</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- 前往商品頁連結 -->
                      <div class="product-detail-actions">
                        <router-link
                          :to="`/client/products/${item.productId}/edit`"
                          class="btn btn-sm btn-outline-primary"
                          @click.stop
                        >
                          <i class="ri-external-link-line"></i>
                          前往商品頁
                        </router-link>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="order.orderSource === 'lockline_commerce'" class="detail-section">
        <h2 class="section-title">電子發票</h2>
        <div class="detail-grid">
          <div class="detail-item">
            <label>發票號碼</label>
            <span>{{ order.invoiceNumber || '尚未開立' }}</span>
          </div>
          <div class="detail-item">
            <label>狀態</label>
            <span>{{ order.isInvoiceVoided ? '已作廢' : (order.invoiceNumber ? '已開立' : '未開立') }}</span>
          </div>
          <div class="detail-item">
            <label>載具</label>
            <span>{{ invoiceCarrierText(order) }}</span>
          </div>
        </div>
        <button
          v-if="canReissueInvoice"
          type="button"
          class="btn btn-primary"
          :disabled="reissuingInvoice"
          @click="reissueInvoice"
        >{{ reissuingInvoice ? '開立中' : '重新開立' }}</button>
      </div>

      <!-- 備註 -->
      <div v-if="order.remarks" class="detail-section">
        <h2 class="section-title">備註</h2>
        <p class="remarks-text">{{ order.remarks }}</p>
      </div>

      <!-- 變更歷程 -->
      <div class="detail-section">
        <h2 class="section-title">
          <i class="ri-history-line"></i>
          變更歷程
        </h2>
        <div v-if="changeLogsLoading" class="loading-state">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else-if="changeLogs.length === 0" class="empty-state">
          <i class="ri-file-list-3-line"></i>
          <p>尚無變更歷程</p>
        </div>
        <div v-else class="change-log-timeline">
          <div
            v-for="log in changeLogs"
            :key="log.id"
            class="change-log-item"
          >
            <div class="change-log-dot" :class="getLogDotClass(log.action)">
              <i :class="getLogIcon(log.action)"></i>
            </div>
            <div class="change-log-content">
              <div class="change-log-header">
                <span class="change-log-action">{{ log.description }}</span>
                <span class="change-log-time">{{ formatDate(log.createdAt) }}</span>
              </div>
              <div class="change-log-meta">
                <span class="change-log-operator">
                  <i class="ri-user-line"></i>
                  {{ log.changedByName || '系統' }}
                </span>
                <span v-if="log.changedFields && log.changedFields.length > 0" class="change-log-fields">
                  異動欄位：{{ log.changedFields.join('、') }}
                </span>
              </div>
              <!-- 快照 diff -->
              <div v-if="log.snapshotBefore && log.action !== 'created'" class="change-log-snapshot">
                <button
                  class="btn-toggle-snapshot"
                  @click="toggleSnapshot(log.id)"
                >
                  <i :class="expandedLogId === log.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  {{ expandedLogId === log.id ? '收起' : '查看變更前後資料' }}
                </button>
                <div v-if="expandedLogId === log.id" class="snapshot-compare">
                  <div class="snapshot-col">
                    <div class="snapshot-col-title">變更前</div>
                    <dl class="snapshot-dl">
                      <template v-for="(val, key) in log.snapshotBefore" :key="key">
                        <dt>{{ getFieldLabel(key) }}</dt>
                        <dd :class="{ 'changed': log.snapshotAfter && String(log.snapshotBefore[key]) !== String(log.snapshotAfter[key]) }">
                          {{ formatSnapshotValue(key, val, log, 'before') }}
                        </dd>
                      </template>
                    </dl>
                  </div>
                  <div class="snapshot-col">
                    <div class="snapshot-col-title">變更後</div>
                    <dl class="snapshot-dl">
                      <template v-for="(val, key) in log.snapshotAfter" :key="key">
                        <dt>{{ getFieldLabel(key) }}</dt>
                        <dd :class="{ 'changed': log.snapshotBefore && String(log.snapshotBefore[key]) !== String(log.snapshotAfter[key]) }">
                          {{ formatSnapshotValue(key, val, log, 'after') }}
                        </dd>
                      </template>
                    </dl>
                  </div>
                </div>
              </div>
              <!-- 建立快照（只顯示建立後資料）-->
              <div v-else-if="log.snapshotAfter && log.action === 'created'" class="change-log-snapshot">
                <button
                  class="btn-toggle-snapshot"
                  @click="toggleSnapshot(log.id)"
                >
                  <i :class="expandedLogId === log.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  {{ expandedLogId === log.id ? '收起' : '查看建立時資料' }}
                </button>
                <div v-if="expandedLogId === log.id" class="snapshot-compare snapshot-single">
                  <div class="snapshot-col">
                    <div class="snapshot-col-title">建立時資料</div>
                    <dl class="snapshot-dl">
                      <template v-for="(val, key) in log.snapshotAfter" :key="key">
                        <dt>{{ getFieldLabel(key) }}</dt>
                        <dd>{{ formatSnapshotValue(key, val, log, 'after') }}</dd>
                      </template>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { orderService } from '@/api/modules/orders.js';
import { orderSourceLabel, paymentMethodLabel } from '@/utils/orderLabels.js';
import { productService } from '@/api/modules/products.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ClientOrderDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const order = ref(null);
    const printing = ref(false);
    const orderItems = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const expandedItemId = ref(null);
    const productDetail = ref(null);
    const productDetailLoading = ref(false);
    const editingOrderStatus = ref('pending_fulfillment');
    const editingPaymentStatus = ref('pending');
    const editingPaidAmount = ref(0);
    const statusUpdating = ref(false);
    const reissuingInvoice = ref(false);
    const canReissueInvoice = computed(() => {
      const current = order.value;
      return current?.invoiceType === 'e_invoice'
        && current.paymentStatus === 'paid'
        && Number(current.paidAmount) > 0
        && !current.invoiceNumber
        && !current.isInvoiceVoided;
    });
    const compensationUpdating = ref(false);

    // 變更歷程
    const changeLogs = ref([]);
    const changeLogsLoading = ref(false);
    const expandedLogId = ref(null);

    const VALID_LOGISTICS_STATUSES = new Set([
      'pending_fulfillment',
      'preparing',
      'shipped',
      'arrived_store',
      'picked_up',
      'delivered',
      'returned_unclaimed',
      'returned_customer',
    ]);

    const LEGACY_STATUS_TO_LOGISTICS = {
      pending: 'pending_fulfillment',
      pending_payment: 'pending_fulfillment',
      paid: 'pending_fulfillment',
      confirmed: 'preparing',
      processing: 'preparing',
      shipped: 'shipped',
      delivered: 'arrived_store',
      picked_up: 'picked_up',
      completed: 'delivered',
      cancelled: 'pending_fulfillment',
      refunded: 'returned_customer',
    };

    const resolveEditingLogisticsStatus = (orderData) => {
      const logisticsStatus = orderData?.logisticsStatus;
      if (logisticsStatus && VALID_LOGISTICS_STATUSES.has(logisticsStatus)) {
        return logisticsStatus;
      }
      const legacyStatus = orderData?.status;
      if (legacyStatus && LEGACY_STATUS_TO_LOGISTICS[legacyStatus]) {
        return LEGACY_STATUS_TO_LOGISTICS[legacyStatus];
      }
      return 'pending_fulfillment';
    };

    const applyOrderToEditors = (orderData) => {
      editingOrderStatus.value = resolveEditingLogisticsStatus(orderData);
      editingPaymentStatus.value = orderData.paymentStatus || 'pending';
      editingPaidAmount.value = Number(orderData.paidAmount || 0);
    };

    const fetchOrder = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await orderService.getOrder(route.params.id);

        if (response && response.success && response.data) {
          order.value = response.data;
          orderItems.value = response.data.items || [];
          applyOrderToEditors(response.data);
        } else {
          const errorMsg = (response && response.message) || '獲取訂單詳情失敗';
          error.value = errorMsg;
        }
      } catch (err) {
        console.error('獲取訂單詳情失敗:', err);
        error.value = err.message || err.data?.message || '獲取訂單詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleBack = () => {
      router.push('/client/orders');
    };

    const toggleItemDetail = async (item) => {
      if (expandedItemId.value === item.id) {
        expandedItemId.value = null;
        productDetail.value = null;
        return;
      }
      expandedItemId.value = item.id;
      productDetail.value = null;
      productDetailLoading.value = true;
      try {
        const [productRes, variantsRes] = await Promise.all([
          productService.getProduct(item.productId),
          item.variantId ? productService.listVariants(item.productId) : Promise.resolve(null)
        ]);
        if (productRes && productRes.success && productRes.data) {
          const product = { ...productRes.data };
          if (variantsRes && variantsRes.success && variantsRes.data) {
            product.variants = variantsRes.data.items || variantsRes.data || [];
          }
          productDetail.value = product;
        }
      } catch (err) {
        console.error('載入商品失敗:', err);
      } finally {
        productDetailLoading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return 'NT$ 0';
      return `NT$ ${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatNumber = (number) => {
      if (number === null || number === undefined) return '0';
      return Number(number).toLocaleString('zh-TW');
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        pending: 'badge badge-warning',
        pending_fulfillment: 'badge badge-warning',
        preparing: 'badge badge-info',
        shipped: 'badge badge-primary',
        arrived_store: 'badge badge-info',
        picked_up: 'badge badge-success',
        delivered: 'badge badge-success',
        returned_unclaimed: 'badge badge-danger',
        returned_customer: 'badge badge-danger',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        pending_fulfillment: '待履約',
        preparing: '備貨中',
        shipped: '已出貨',
        arrived_store: '已到店',
        picked_up: '客戶已取貨',
        delivered: '宅配已送達',
        returned_unclaimed: '到店未取退回',
        returned_customer: '客戶退貨',
      };
      return statusMap[status] || status;
    };

    const getPaymentStatusBadgeClass = (status) => {
      const statusMap = {
        pending: 'badge badge-warning',
        paid: 'badge badge-success',
        failed: 'badge badge-secondary',
        refunded: 'badge badge-danger',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getPaymentStatusText = (status) => {
      const statusMap = {
        pending: '待付款',
        paid: '已付款',
        failed: '付款失敗',
        refunded: '已退款',
      };
      return statusMap[status] || status;
    };

    const syncPaidAmountFromStatus = () => {
      const total = Number(order.value?.totalAmount || 0);
      if (
        editingPaymentStatus.value === 'paid' ||
        ['picked_up', 'delivered', 'returned_unclaimed', 'returned_customer'].includes(editingOrderStatus.value)
      ) {
        editingPaidAmount.value = total;
      } else if (
        editingPaymentStatus.value === 'pending' &&
        Number(editingPaidAmount.value || 0) > total
      ) {
        editingPaidAmount.value = 0;
      }
    };

    const handleOrderStatusChange = () => {
      syncPaidAmountFromStatus();
    };

    const handlePaymentStatusChange = () => {
      syncPaidAmountFromStatus();
    };

    const invoiceCarrierText = (current) => {
      const labels = { mobile: '手機載具', email: 'E-mail', company_email: 'E-mail', none: '隨貨寄出' };
      return labels[current?.eInvoiceCarrierType] || '-';
    };

    const reissueInvoice = async () => {
      if (!order.value?.id) return;
      reissuingInvoice.value = true;
      try {
        const response = await orderService.reissueEinvoice(order.value.id);
        if (response?.success && response.data) {
          order.value = response.data;
          await showSuccess('開立成功', '電子發票已開立');
        } else {
          await showError('開立失敗', response?.message || '電子發票未開立');
        }
      } catch (err) {
        await showError('開立失敗', err.response?.data?.message || err.message || '電子發票未開立');
      } finally {
        reissuingInvoice.value = false;
      }
    };

    const handleUpdateOrderStatus = async () => {
      if (!order.value?.id) return;

      syncPaidAmountFromStatus();

      const total = Number(order.value.totalAmount || 0);
      const paidAmount = Number(editingPaidAmount.value || 0);

      if (editingPaymentStatus.value === 'paid' && paidAmount < total) {
        await showError('驗證失敗', '付款狀態為已付款時，已付金額需等於或大於總金額');
        return;
      }
      if (editingPaymentStatus.value !== 'paid' && paidAmount > total) {
        await showError('驗證失敗', '未付款或非已付款狀態時，已付金額不可大於總金額');
        return;
      }

      statusUpdating.value = true;
      try {
        const payload = {
          logisticsStatus: editingOrderStatus.value,
          paymentStatus: editingPaymentStatus.value,
          paidAmount,
        };
        const response = await orderService.updateOrderStatus(order.value.id, payload);
        if (response && response.success && response.data) {
          order.value = response.data;
          orderItems.value = response.data.items || [];
          applyOrderToEditors(response.data);
          await fetchChangeLogs();
          await showSuccess('更新成功', '訂單狀態已更新');
        } else {
          await showError('更新失敗', response?.message || '訂單狀態更新失敗');
        }
      } catch (err) {
        console.error('更新訂單狀態失敗:', err);
        await showError('更新失敗', err.response?.data?.message || err.message || '訂單狀態更新失敗');
      } finally {
        statusUpdating.value = false;
      }
    };

    const getPaymentMethodText = paymentMethodLabel;
    const getOrderSourceText = orderSourceLabel;

    const getOrderLifecycle = (orderData) => {
      const raw = orderData?.rawData;
      if (!raw) return null;
      if (typeof raw === 'object') {
        return raw.inventoryLifecycle || null;
      }
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return parsed?.inventoryLifecycle || null;
        } catch {
          return null;
        }
      }
      return null;
    };

    const getInventoryLifecycleText = (orderData) => {
      const lifecycle = getOrderLifecycle(orderData);
      if (!lifecycle) return '未啟用';
      if (lifecycle.paidOutOfStock) return '已付款但缺貨';
      if (lifecycle.deducted) return '已實扣';
      if (lifecycle.released) return '已釋放預留';
      if (lifecycle.reserved) return '已預留';
      if (lifecycle.virtualReserved) return '待出貨扣庫';
      if (lifecycle.isBackorder) return '缺貨預訂';
      return '未處理';
    };

    const getInventoryLifecycleBadgeClass = (orderData) => {
      const lifecycle = getOrderLifecycle(orderData);
      if (!lifecycle) return 'badge badge-secondary';
      if (lifecycle.paidOutOfStock) return 'badge badge-danger';
      if (lifecycle.deducted) return 'badge badge-success';
      if (lifecycle.released) return 'badge badge-secondary';
      if (lifecycle.reserved || lifecycle.virtualReserved) return 'badge badge-info';
      if (lifecycle.isBackorder) return 'badge badge-warning';
      return 'badge badge-secondary';
    };

    const getInventoryLifecycleHint = (orderData) => {
      const lifecycle = getOrderLifecycle(orderData);
      if (!lifecycle) return '此訂單尚未記錄庫存狀態機資訊。';
      if (lifecycle.paidOutOfStock) return '付款成功後預留失敗，請優先處理退款或改單。';
      if (lifecycle.deducted) return '此訂單已完成出貨扣庫。';
      if (lifecycle.released) return '此訂單已取消並釋放預留庫存。';
      if (lifecycle.reserved) return '此訂單已預留庫存，改為「已出貨」後將實際扣庫。';
      if (lifecycle.virtualReserved) return '簡易模式：改為「已出貨」後才會實際扣減商品庫存。';
      if (lifecycle.isBackorder) return '缺貨預訂單，待補貨後再手動推進出貨流程。';
      return '庫存流程尚未執行。';
    };

    const canHandlePaidOutOfStock = (orderData) => {
      const lifecycle = getOrderLifecycle(orderData);
      return Boolean(lifecycle?.paidOutOfStock);
    };

    const handlePaidOutOfStockCompensation = async (action) => {
      if (!order.value?.id || compensationUpdating.value) return;
      compensationUpdating.value = true;
      try {
        const response = await orderService.handlePaidOutOfStockCompensation(order.value.id, { action });
        if (response && response.success && response.data) {
          order.value = response.data;
          orderItems.value = response.data.items || [];
          applyOrderToEditors(response.data);
          await fetchChangeLogs();
          await showSuccess('處理成功', action === 'refunded' ? '已標記為退款處理' : '已標記為轉預購處理');
        } else {
          await showError('處理失敗', response?.message || '缺貨補償處理失敗');
        }
      } catch (err) {
        console.error('缺貨補償處理失敗:', err);
        await showError('處理失敗', err.response?.data?.message || err.message || '缺貨補償處理失敗');
      } finally {
        compensationUpdating.value = false;
      }
    };

    const getPointsStatusText = (status) => {
      const statusMap = {
        pending: '待發放',
        earned: '已發放',
        expired: '已過期',
      };
      return statusMap[status] || status || '-';
    };

    const formatEffectiveDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };

    const getOrderPointsStatusText = (orderData) => {
      const summary = orderData?.pointsEarnSummary;
      if (!orderData?.pointsEarned || orderData.pointsEarned <= 0) return '-';
      if (orderData.pointsStatus === 'pending' && !summary) return '待發放';
      if (summary?.pendingEarnPoints > 0 && summary?.activeEarnPoints > 0) {
        return '部分可用、部分待生效';
      }
      if (summary?.pendingEarnPoints > 0) return '已發放（待生效）';
      if (summary?.activeEarnPoints > 0 || orderData.pointsStatus === 'earned') return '已發放（可用）';
      return getPointsStatusText(orderData.pointsStatus);
    };

    // 載入變更歷程
    const fetchChangeLogs = async () => {
      changeLogsLoading.value = true;
      try {
        const res = await orderService.getOrderChangeLogs(route.params.id);
        if (res && res.success && Array.isArray(res.data)) {
          changeLogs.value = res.data;
        }
      } catch (err) {
        console.error('載入訂單變更歷程失敗:', err);
      } finally {
        changeLogsLoading.value = false;
      }
    };

    const toggleSnapshot = (logId) => {
      expandedLogId.value = expandedLogId.value === logId ? null : logId;
    };

    const FIELD_LABELS = {
      status:        '訂單狀態',
      paymentStatus: '付款狀態',
      paymentMethod: '付款方式',
      paidAmount:    '已付金額',
      totalAmount:   '總金額',
      discountAmount:'折扣金額',
      remarks:       '備註',
      memberId:      '會員',
      storeId:       '門市',
      trackingNumber:'追蹤編號',
      orderNumber:   '訂單編號',
      orderDate:     '訂單日期',
      subtotal:      '小計',
    };

    const getFieldLabel = (key) => FIELD_LABELS[key] || key;

    const shipmentNumber = (orderData) => orderData?.trackingNumber || '-';

    const canPrintLogistics = (orderData) => orderData?.shippingMethod === 'ecpay'
      && String(orderData?.logisticsId || '').trim() !== '';

    const writeLogisticsHtml = (popup, html) => {
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
    };

    const printLogistics = async () => {
      if (!order.value?.id || printing.value) return;
      const popup = window.open('', '_blank');
      if (!popup) {
        await showError('無法開啟託運單', '請允許彈出視窗後再列印');
        return;
      }
      printing.value = true;
      try {
        const response = await orderService.printLogisticsDocuments([order.value.id]);
        const html = response?.data?.html;
        if (!response?.success || !html) {
          popup.close();
          await showError('無法列印託運單', response?.message || '綠界沒有回傳託運單');
          return;
        }
        writeLogisticsHtml(popup, html);
        await fetchOrder();
      } catch (err) {
        popup.close();
        await showError('無法列印託運單', err.message || '綠界沒有回傳託運單');
      } finally {
        printing.value = false;
      }
    };

    // side: 'before' | 'after'
    const formatSnapshotValue = (key, val, log, side) => {
      if (val === null || val === undefined) return '-';
      if (key === 'paidAmount' || key === 'totalAmount' || key === 'discountAmount' || key === 'subtotal') {
        return `NT$ ${Number(val).toLocaleString('zh-TW')}`;
      }
      if (key === 'orderDate') return formatDate(val);
      if (key === 'status') return getStatusText(val);
      if (key === 'paymentStatus') return getPaymentStatusText(val);
      if (key === 'paymentMethod') return getPaymentMethodText(val);
      if (key === 'storeId') {
        if (!val) return '（未指定）';
        const name = side === 'before' ? log?.storeNameBefore : log?.storeNameAfter;
        return name || val;
      }
      if (key === 'memberId') {
        if (!val) return '（未指定）';
        const name = side === 'before' ? log?.memberNameBefore : log?.memberNameAfter;
        return name || val;
      }
      return String(val);
    };

    const getLogDotClass = (action) => {
      const map = {
        created:                'dot-created',
        status_changed:         'dot-status',
        payment_status_changed: 'dot-payment',
        updated:                'dot-updated',
      };
      return map[action] || 'dot-updated';
    };

    const getLogIcon = (action) => {
      const map = {
        created:                'ri-add-circle-line',
        status_changed:         'ri-refresh-line',
        payment_status_changed: 'ri-secure-payment-line',
        updated:                'ri-edit-line',
      };
      return map[action] || 'ri-edit-line';
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        fixed_amount: '固定折扣',
        percentage: '折扣百分比',
        free_shipping: '免運費',
        gift: '贈品',
      };
      return typeMap[type] || type || '-';
    };

    const formatCouponDiscount = (usage) => {
      if (!usage.couponType || !usage.discountValue) return '-';
      if (usage.couponType === 'percentage') {
        return `${usage.discountValue}% 折扣`;
      }
      if (usage.couponType === 'fixed_amount') {
        return `NT$ ${Number(usage.discountValue).toLocaleString('zh-TW')} 折扣`;
      }
      if (usage.couponType === 'free_shipping') return '免運費';
      return `${usage.discountValue}${usage.discountUnit || ''}`;
    };

    onMounted(() => {
      fetchOrder();
      fetchChangeLogs();
    });

    return {
      order,
      printing,
      orderItems,
      loading,
      error,
      expandedItemId,
      productDetail,
      productDetailLoading,
      editingOrderStatus,
      editingPaymentStatus,
      editingPaidAmount,
      statusUpdating,
      reissuingInvoice,
      canReissueInvoice,
      invoiceCarrierText,
      reissueInvoice,
      compensationUpdating,
      changeLogs,
      changeLogsLoading,
      expandedLogId,
      handleBack,
      toggleItemDetail,
      toggleSnapshot,
      getFieldLabel,
      shipmentNumber,
      canPrintLogistics,
      printLogistics,
      formatSnapshotValue,
      getLogDotClass,
      getLogIcon,
      formatDate,
      formatCurrency,
      formatNumber,
      getStatusBadgeClass,
      getStatusText,
      getPaymentStatusBadgeClass,
      getPaymentStatusText,
      getPaymentMethodText,
      getOrderSourceText,
      getInventoryLifecycleText,
      getInventoryLifecycleBadgeClass,
      getInventoryLifecycleHint,
      canHandlePaidOutOfStock,
      handlePaidOutOfStockCompensation,
      getPointsStatusText,
      formatEffectiveDate,
      getOrderPointsStatusText,
      handleOrderStatusChange,
      handlePaymentStatusChange,
      handleUpdateOrderStatus,
      getCouponTypeText,
      formatCouponDiscount,
      fetchOrder,
    };
  },
};
</script>

<style scoped>
.order-detail {
  max-width: 1200px;
}

.detail-section {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.detail-item span {
  font-size: 16px;
  color: #333;
}

.text-bold {
  font-weight: 600;
  font-size: 18px;
}

.remarks-text {
  color: #666;
  line-height: 1.6;
  white-space: pre-wrap;
}

.table-wrapper {
  margin-top: 16px;
  overflow-x: auto;
}

.table-wrapper table {
  width: 100%;
  border-collapse: collapse;
}

.table-wrapper th,
.table-wrapper td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.table-wrapper th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

/* 可展開的商品列 */
.item-row.expandable {
  cursor: pointer;
  transition: background 0.15s;
}

.item-row.expandable:hover {
  background: #f5f8ff;
}

.item-row.expanded {
  background: #eef3ff;
}

.expand-cell {
  width: 36px;
  text-align: center;
}

.expand-icon {
  color: #4a90d9;
  font-size: 18px;
}

.product-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.variant-badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 11px;
  background: #e8f0fe;
  color: #3a6bc9;
  border-radius: 4px;
  font-weight: 500;
}

/* 展開面板 */
.item-detail-row td {
  padding: 0 !important;
  border-bottom: none;
}

.item-detail-cell {
  background: #f8faff;
  border-bottom: 2px solid #c8d8f8 !important;
}

.product-detail-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 24px;
  color: #666;
  font-size: 14px;
}

.product-detail-panel {
  padding: 20px 24px;
}

.product-detail-main {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.product-detail-image {
  width: 90px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-detail-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-detail-image--empty {
  color: #bbb;
  font-size: 32px;
}

.product-detail-info {
  flex: 1;
}

.product-detail-name {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 8px;
}

.product-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #555;
}

.meta-item i {
  color: #4a90d9;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background: #d4edda;
  color: #155724;
}

.status-inactive {
  background: #f8d7da;
  color: #721c24;
}

.product-detail-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin-top: 6px;
  max-height: 60px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.product-variant-highlight {
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #c8d8f8;
  border-radius: 6px;
  max-width: 340px;
}

.variant-highlight-title {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin-bottom: 6px;
}

.variant-highlight-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.variant-name {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.variant-price {
  font-size: 13px;
  color: #4a90d9;
  font-weight: 500;
}

.variant-price--inherited {
  color: #999;
  font-style: italic;
}

.product-detail-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

.btn-outline-primary {
  border: 1px solid #4a90d9;
  color: #4a90d9;
  background: transparent;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.btn-outline-primary:hover {
  background: #4a90d9;
  color: #fff;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.error-message {
  color: #dc3545;
  margin-bottom: 16px;
}

/* ── 變更歷程 ── */
.section-title i {
  margin-right: 6px;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #999;
}

.empty-state i {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}

.change-log-timeline {
  position: relative;
  padding-left: 36px;
}

.change-log-timeline::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e8edf2;
}

.change-log-item {
  position: relative;
  margin-bottom: 24px;
}

.change-log-item:last-child {
  margin-bottom: 0;
}

.change-log-dot {
  position: absolute;
  left: -36px;
  top: 2px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #fff;
  z-index: 1;
}

.dot-created  { background: #28a745; }
.dot-status   { background: #4a90d9; }
.dot-payment  { background: #f5a623; }
.dot-updated  { background: #6c757d; }

.change-log-content {
  background: #f8f9fa;
  border: 1px solid #e8edf2;
  border-radius: 8px;
  padding: 12px 16px;
}

.change-log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.change-log-action {
  font-weight: 600;
  color: #222;
  font-size: 14px;
}

.change-log-time {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.change-log-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
  flex-wrap: wrap;
}

.change-log-operator i {
  margin-right: 3px;
}

.change-log-fields {
  color: #4a90d9;
}

/* 快照展開 */
.change-log-snapshot {
  margin-top: 10px;
}

.btn-toggle-snapshot {
  background: none;
  border: none;
  color: #4a90d9;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-toggle-snapshot:hover {
  text-decoration: underline;
}

.snapshot-compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 10px;
  border-top: 1px solid #e8edf2;
  padding-top: 10px;
}

.snapshot-compare.snapshot-single {
  grid-template-columns: 1fr;
}

.snapshot-col-title {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
}

.snapshot-dl {
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 12px;
  font-size: 12px;
}

.snapshot-dl dt {
  color: #888;
  white-space: nowrap;
}

.snapshot-dl dd {
  margin: 0;
  color: #333;
}

.snapshot-dl dd.changed {
  color: #e65c00;
  font-weight: 600;
}

.payment-update-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  align-items: end;
}

.payment-update-actions {
  display: flex;
  align-items: flex-end;
}

.status-adjust-hint {
  font-size: 13px;
  color: #666;
  margin: -8px 0 16px;
  line-height: 1.5;
}

.inventory-lifecycle-card {
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #e8edf2;
  border-radius: 8px;
  background: #fafcff;
}

.inventory-lifecycle-title {
  font-size: 13px;
  color: #555;
  font-weight: 600;
  margin-bottom: 8px;
}

.inventory-lifecycle-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inventory-lifecycle-hint {
  margin: 0;
  color: #666;
  font-size: 12px;
  line-height: 1.5;
}

.inventory-lifecycle-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.text-warning-strong {
  color: #d97706;
  font-weight: 600;
}

.text-danger-strong {
  color: #dc3545;
  font-weight: 600;
}

.coupon-code-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
  font-weight: 600;
  letter-spacing: 0.5px;
}
</style>
